import { FC, PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { AppState, Auth0Provider, User, useAuth0 } from '@auth0/auth0-react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { LoadingFullScreen } from '@/components/helpers/loading';
import { useGetCurrentRolesQuery } from '@/redux/back-dashboard-api';
import { useGetUserProfileQuery } from '@/redux/graph/hooks';
import { useAppDispatch } from '@/redux/hooks';
import { setRoles, setSession } from '@/redux/features/session/slice';
import { env } from '@/lib/env';
import { setAuth0TokenGetter } from '@/lib/auth0-token';
import { useSession } from '@/redux/features/session/hooks';
import { InvitationAuthProvider } from './invitation-provider';
import { MOCK_USER } from '@/lib/constants';
import { GetCurrentSessionResponse } from '@/typings/api';
import { UserRole } from '@pcmp-checka/lib-utilities/atkomst';
import { isMfeMode, getMfeMountProps } from '@/mfe/mfe-mode';

const buildSession = (
  user: User,
  accessToken: string
): GetCurrentSessionResponse => ({
  user: {
    'https://accounts.ikea.com/email': user.email ?? '',
    'https://accounts.ikea.com/upn': user.email ?? '',
    'https://accounts.ikea.com/guid': '',
    'https://accounts.ikea.com/networkid': user.email ?? '',
    given_name: user.given_name ?? '',
    family_name: user.family_name ?? '',
    nickname: user.nickname ?? '',
    name: user.name ?? user.email ?? '',
    picture: user.picture ?? '',
    updated_at: user.updated_at ?? new Date().toISOString(),
    email: user.email ?? '',
    email_verified: user.email_verified ?? false,
    sub: user.sub ?? '',
    sid: '',
    manager: {
      displayName: '',
      id: '',
      mail: '',
    },
  },
  accessToken,
  accessTokenExpiresAt: Number.MAX_SAFE_INTEGER,
  accessTokenScope: 'openid profile email offline_access',
  idToken: '',
  idTokenExpiresAt: Number.MAX_SAFE_INTEGER,
  tokenType: 'Bearer',
});

export const RolesProvider: FC = () => {
  const dispatch = useAppDispatch();
  const { session } = useSession();
  const { data } = useGetCurrentRolesQuery(undefined, {
    skip: !session?.accessToken,
  });

  useEffect(() => {
    if (data) {
      dispatch(setRoles(data.roles.roles as UserRole[]));
    }
  }, [data, dispatch]);

  return null;
};

const SessionProfileProvider: FC = () => {
  const { session } = useSession();

  useGetUserProfileQuery(
    { email: session?.user.email ?? '' },
    { skip: !session?.user.email }
  );

  return null;
};

export const AuthConsumer: FC<PropsWithChildren> = ({ children }) => {
  const {
    isAuthenticated,
    isLoading,
    user,
    getAccessTokenSilently,
    loginWithRedirect,
    error,
  } = useAuth0();
  const sessionState = useSession();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !error) {
      loginWithRedirect({ appState: { returnTo: pathname } });
    }
  }, [error, isAuthenticated, isLoading, loginWithRedirect]);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      return;
    }

    // Registers a way for non-React code (e.g. RTK Query base query) to force
    // a token refresh when the API rejects the current access token.
    setAuth0TokenGetter(getAccessTokenSilently);

    getAccessTokenSilently()
      .then((accessToken) => {
        dispatch(setSession(buildSession(user, accessToken)));
      })
      .catch((e) => {
        // ponytail: silent refresh failed (expired/invalid refresh token) -> force a
        // fresh SSO login instead of leaving the user stuck with no session.
        console.error(e);
        loginWithRedirect({ appState: { returnTo: pathname } });
      });

    return () => setAuth0TokenGetter(null);
  }, [
    dispatch,
    getAccessTokenSilently,
    isAuthenticated,
    loginWithRedirect,
    pathname,
    user,
  ]);

  if (isLoading) {
    return <LoadingFullScreen />;
  }

  if (error && !isAuthenticated) {
    return <LoadingFullScreen textI18nKey="label.loadingUserProfile" />;
  }

  if (isAuthenticated && sessionState.session?.accessToken) {
    return (
      <>
        <SessionProfileProvider />
        <RolesProvider />
        {children}
      </>
    );
  }

  return null;
};

const Auth0ProviderWithNavigate: FC<PropsWithChildren> = ({ children }) => {
  const navigate = useNavigate();

  const onRedirectCallback = useCallback(
    (appState?: AppState) => {
      navigate(appState?.returnTo || window.location.pathname);
    },
    [navigate]
  );

  return (
    <Auth0Provider
      domain={env.AUTH0_DOMAIN}
      clientId={env.AUTH0_CLIENT_ID}
      useRefreshTokens
      cacheLocation="localstorage"
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: env.AUTH0_AUDIENCE,
        scope: 'openid profile email offline_access',
      }}
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
};

const MfeAuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const dispatch = useAppDispatch();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const mountProps = getMfeMountProps();
    const { user } = mountProps;

    mountProps
      .authToken([env.AUTH0_AUDIENCE])
      .then((accessToken) => {
        dispatch(
          setSession({
            user: {
              'https://accounts.ikea.com/email': user.email,
              'https://accounts.ikea.com/upn': user.email,
              'https://accounts.ikea.com/guid': user.id,
              'https://accounts.ikea.com/networkid': user.email,
              given_name: user.displayName.split(' ')[0] ?? '',
              family_name: user.displayName.split(' ').slice(1).join(' ') ?? '',
              nickname: user.displayName,
              name: user.displayName,
              picture: '',
              updated_at: new Date().toISOString(),
              email: user.email,
              email_verified: true,
              sub: user.id,
              sid: '',
              manager: { displayName: '', id: '', mail: '' },
            },
            accessToken,
            accessTokenExpiresAt: Number.MAX_SAFE_INTEGER,
            accessTokenScope: 'openid profile email',
          })
        );
        setIsReady(true);
      })
      .catch((e) => {
        console.error('Failed to get access token from MFE mount props', e);
        Sentry.captureException(e);
      });
  }, []);

  if (!isReady) return <LoadingFullScreen />;

  return (
    <>
      <RolesProvider />
      {children}
    </>
  );
};

export const MockAuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(setSession(buildSession(MOCK_USER, 'mock-access-token')));
  }, [dispatch]);

  return (
    <>
      <SessionProfileProvider />
      <RolesProvider />
      {children}
    </>
  );
};

export const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  if (isMfeMode()) {
    return <MfeAuthProvider>{children}</MfeAuthProvider>;
  }

  return <AuthProviderMain>{children}</AuthProviderMain>;
};

const AuthProviderMain: FC<PropsWithChildren> = ({ children }) => {
  const { invitationToken } = useSession();

  if (invitationToken) {
    return <InvitationAuthProvider>{children}</InvitationAuthProvider>;
  }

  if (env.MOCK_AUTH) {
    return <MockAuthProvider>{children}</MockAuthProvider>;
  }

  return (
    <Auth0ProviderWithNavigate>
      <AuthConsumer>{children}</AuthConsumer>
    </Auth0ProviderWithNavigate>
  );
};

export default AuthProvider;
